<template>
    <div class="wizard-wrapper flex flex-wrap relative shadow-card rounded-tr-lg rounded-br-lg"
         :data-test="`wizard-${dataTestName}`"
         :class="{
            'wizard-height-sm': height === 'sm',
            'wizard-height-lg': height === 'lg',
         }">
        <div class="wizard-step flex flex-wrap" :class="{'full-width': !showSummary}" v-loading="dataLoading">
            <div class="w-full pt-7 px-5 flex justify-between" v-if="showTopBar">
                <span class="steps-topbar-pagination text-sm main-text-color">
                    {{ $t('general.step') }} {{ activeIndex + 1 }} {{ $t('general.of') }} {{ steps.length }}
                </span>
                <span class="steps-topbar-pagination text-sm">
                    <el-tooltip
                        :content="$t(`reportCreation.${helpData[activeIndex]}.help`)"
                        :open-delay="100"
                        class="item"
                        effect="dark"
                        placement="top"
                    >
                        <div class="flex items-center">
                            <i
                                class="vc-icon-info text-primary text-xl mx-2"
                            />
                            <div class="break-normal text-primary">{{ $t('general.help') }}</div>
                        </div>
                    </el-tooltip>
                </span>
            </div>

            <div class="steps-wrapper w-full xxl:px-24 px-5">
                <slot></slot>
            </div>
            <div class="w-full navigation">
                <div class="m-5">
                    <div class="flex justify-between">
                        <base-button
                            outline
                            fixed-width="w-37"
                            @click="onCancel"
                            v-if="onFirstStep"
                        >
                            <div class="flex items-center">
                                <IconDiscard class="button-icon"/>
                                <span class="mx-1 text-base font-bold">{{ $t('common.cancel') }}</span>
                            </div>
                        </base-button>
                        <base-button
                            outline
                            fixed-width="w-37"
                            @click="onPrevious"
                            v-if="!onFirstStep && showPreviousButton"
                        >
                            <div class="flex items-center">
                                <i
                                    class="icon-base button-icon"
                                    :class="$rtl.isRTL ? 'vc-icon-next' : 'vc-icon-back'"
                                />
                                <span class="mx-1 text-base font-bold">{{ $t('general.back') }}</span>
                            </div>
                        </base-button>
                        <div class="flex">
                            <slot name="extra-actions"></slot>
                            <base-button
                                v-if="!onLastStep"
                                @click="onNext"
                                data-test-name="wizard-next"
                                :disabled="!nextButtonCheck"
                                type="primary"
                            >
                                {{ $t('general.next') }}
                                <i
                                    class="icon-base button-icon"
                                    :class="$rtl.isRTL ? 'vc-icon-back' : 'vc-icon-next'"
                                />
                            </base-button>
                            <base-button
                                v-if="onLastStep"
                                @click="onFinish"
                                :loading="dataLoading"
                                data-test-name="wizard-finish"
                                type="primary"
                            >
                                <IconSave class="button-icon"/>
                                {{ $t('general.finish') }}
                            </base-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="main-bg-item-dark-color wizard-summary"
            v-if="showSummary"
        >
            <wizard-summary
                :active="activeIndex"
                ref="summary"
                :model="model"
                :steps="steps"
            >

                <slot-renderer v-for="(step, index) in steps" :key="index" :step="step"/>

            </wizard-summary>
        </div>
    </div>
</template>

<script>
    import { wizardMixin } from '@/mixins/wizardMixin';
    import WizardSummary from './WizardSummary';
    import WizardSummaryStep from './WizardSummaryStep';
    import { Tooltip } from 'element-ui'

    export default {
        name: 'default-wizard',
        props: {
            name: {
                type: String,
                default: ''
            },
            dataTestName: {
                type: String,
                default: ''
            }
        },
        mixins: [wizardMixin],
        data () {
            return {
                helpData: {
                    0: 'reportData',
                    1: 'widget',
                    2: 'schedule'
                }
            }
        },
        components: {
            WizardSummary,
            slotRenderer: {
                props: ['step'],
                components: {
                    WizardSummaryStep
                },
                render(h) {
                    return h('wizard-summary-step', [
                        h('template', {slot: 'header'}, [this.step.$slots.header]),
                        h('template', {slot: 'summary'}, [this.step.$slots.summary])
                    ])
                }
            },
            [Tooltip.name]: Tooltip
        },
        methods: {
            onCancel () {
                this.$emit('on-cancel')
            }
        }
    }
</script>

<style lang="scss" scoped>
    $summary-width-lg: 425px;
    $summary-width-md: 300px;
    $summary-width-sm: 90px;
    $wizard-max-height: 800px;
    $wizard-height: 670px;

    .wizard-step {
        width: calc(100% - #{$summary-width-sm});
        background: var(--mainBgColor-color);

        @screen md {
            width: calc(100% - #{$summary-width-md});
        }

        @screen lg {
            width: calc(100% - #{$summary-width-lg});
        }

        &.full-width {
            width: 100%;
        }
    }

    [dir="rtl"] .wizard-step {
        @apply rounded-tr-lg rounded-br-lg;
    }
    [dir="ltr"] .wizard-step {
        @apply rounded-tl-lg rounded-bl-lg;
    }

    [dir="rtl"] .wizard-summary {
        @apply rounded-tl-lg rounded-bl-lg;
    }
    [dir="ltr"] .wizard-summary {
        @apply rounded-tr-lg rounded-br-lg;
    }

    .wizard-summary {
        height: $wizard-height;
        max-height: $wizard-max-height;
        overflow: auto;
        width: $summary-width-sm;

        @screen md {
            width: $summary-width-md;
        }

        @screen lg {
            width: $summary-width-lg;
        }
    }

    .steps-topbar-pagination {
        line-height: 17px;
    }

    .steps-topbar-get-help {
        @apply text-active-elements text-base font-bold ml-1 select-none;
        line-height: 17px;
    }

    .steps-wrapper {
        min-height: 500px;
        position: relative;
        overflow: hidden;
    }

    ::v-deep .step-content-wrapper {
        overflow-y: auto;
        min-height: 480px;
        max-height: calc(#{$wizard-max-height} - 124px - 60px);
    }

    .navigation {
        @apply border-t-2 border-field-borders;
    }

    .actions-create-checkbox {
        @apply ml-6;
    }

    .rtl {
        .actions-create-checkbox {
            @apply ml-0 mr-6;
        }
    }

    .wizard-wrapper {

        $wizard-max-height: 500px;
        &.wizard-height-sm {
            .wizard-summary {
                max-height: $wizard-max-height;
            }

            ::v-deep .step-content-wrapper {
            max-height: calc(#{$wizard-max-height} - 124px - 60px);
        }
        }

        $wizard-max-height: 750px;
        &.wizard-height-lg {
            .wizard-summary {
                max-height: $wizard-max-height;
            }

            ::v-deep .step-content-wrapper {
                max-height: calc(#{$wizard-max-height} - 124px - 60px);
            }
        }
    }
.shadow-card {
    @apply mb-3 shadow-base;
}
.button-icon {
    @apply mx-2;
}

[dir="ltr"] .shadow-card {
    @apply mr-3;
}
[dir="rtl"] .shadow-card {
    @apply ml-3;
}
</style>
